import {customElement, property, queryAll, state} from "lit/decorators.js";
import {resolve} from "../../../container";
import {ScrollService} from "../../../common/scroll";

import {AutoInitializing, UnLitElement, WithEopSlots} from "../../../common/elements";
import {ManagingResources} from "../../../common/lifetime";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {ResizeObserverFactory} from "../../../common/observation";
import {noop} from "../../../common/utils/functions";
import {TrackingEventType} from "../../../tracking/types";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingAccordionInteract} from "../../../tracking/trackingAttributes";

//has to be the same duration as expansion animation in scss
export const EXPANSION_DURATION = 500;
export const NEGATIVE_MARGIN = 32;

export type AccordionItemConfiguration = {
    trackingLabel: string;
    headlineSlotId: string;
    contentSlotId: string;
};

export type AccordionConfiguration = {
    openingMode: string;
    accordionItems: AccordionItemConfiguration[];
};

export enum ToggleState {
    CLOSE, OPEN
}

@customElement("eop-accordion")
export class EopAccordion extends WithEopSlots(AutoInitializing(ManagingResources(UnLitElement))) {

    @property({attribute: "accordion-config", type: Array})
    private accordionConfiguration: AccordionConfiguration;

    @state()
    private toggleStates: { [index: number]: ToggleState } = {};

    @queryAll(".accordion-item")
    private items: NodeListOf<HTMLElement>;

    private allowOnlyOneOpened: boolean;
    private verticalAligned: boolean;
    private layoutElement: Element;
    private previousLayoutElementHeight: number;
    private resizeObserver: ResizeObserver;

    public constructor(
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private scrollService: ScrollService = resolve(ScrollService)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.allowOnlyOneOpened = this.accordionConfiguration.openingMode === "single";
        this.resizeObserver = this.resizeObserverFactory.create(() => this.updateHeights());
        this.verticalAligned = !!this.closestThat(e => e.classList.contains("vertical-align"));
        this.layoutElement = this.closestThat(e => e.classList.contains("layout-element"))!;
        this.toggleStates = this.accordionConfiguration.accordionItems.map(() => ToggleState.CLOSE);
    }

    public render(): TemplateResult {
        return html`
            <div class="accordion">${this.renderItems()}</div>
        `;
    }

    public renderItems(): TemplateResult[] {
        return this.accordionConfiguration.accordionItems.map((accordionItem, index) => this.renderItem(index, accordionItem));
    }

    public renderItem(index: number, accordionItem: AccordionItemConfiguration): TemplateResult {
        const handleToggle = (e: Event): void => this.toggle(e, index);
        return html`
            <div class="accordion-item" ?open=${this.toggleStates[index] === ToggleState.OPEN}>
                <div tabindex="0"
                     class="tabbable accordion-item-headline-container"
                     @click=${handleToggle}
                     @keydown=${handleToggle}
                     ${trackingAccordionInteract(accordionItem.trackingLabel, this.toggleStates[index] === ToggleState.OPEN ? TrackingEventType.OPEN : TrackingEventType.CLOSE)}
                >
                    ${this.resolveSlot(accordionItem.headlineSlotId)}
                </div>
                <div class="accordion-item-content-container">
                    ${this.resolveSlot(accordionItem.contentSlotId)}
                </div>
            </div>
            ${this.renderSeparator(index)}
        `;
    }

    private renderSeparator(index: number): Nullable<TemplateResult> {
        if (index < Object.entries(this.toggleStates).length - 1) {
            return html`
                <div class="separator-line"></div>`;
        }
        return null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.updateHeights();
        this.items.forEach(item => this.resizeObserver.observe(this.assignedItemForAccordionItem(item)));
    }

    private updateHeights(): void {
        this.items.forEach(item => this.setCalculatedHeight(item));
    }

    private setCalculatedHeight(item: HTMLElement): void {
        const height = this.assignedItemForAccordionItem(item).clientHeight;
        item.style.setProperty("--collapsible-height", `${height}px`);
    }

    private assignedItemForAccordionItem(item: HTMLElement): HTMLElement {
        return item.querySelector<HTMLElement>(".accordion-item-content")!;
    }

    private toggle(e: Event, index: number): void {
        if (e instanceof KeyboardEvent && e.key !== " " && e.key !== "Enter" && e.key !== "Escape") {
            return;
        }

        const target = e.target as HTMLElement;
        const toggledItem = target.closest(".accordion-item") as HTMLElement;
        const open = this.toggleStates[index] === ToggleState.OPEN;
        this.previousLayoutElementHeight = this.layoutElement.clientHeight;

        if (open) {
            this.scrollToItem(toggledItem, this.offsetFromVerticalAlignmentWhenClosing(toggledItem));
            this.toggleStates = {...this.toggleStates, [index]: ToggleState.CLOSE};
        } else {
            const previouslyOpenedIndex = this.getPreviouslyOpenedItem(index);
            if (previouslyOpenedIndex !== null && this.allowOnlyOneOpened) {
                const previouslyOpenedItem = this.items.item(previouslyOpenedIndex);
                if (previouslyOpenedItem) {
                    this.scrollToItem(toggledItem,
                        this.calculateClosedMarginBetween(toggledItem, previouslyOpenedItem) +
                        this.offsetFromVerticalAlignmentWhenClosing(previouslyOpenedItem) +
                        this.offsetFromVerticalAlignmentWhenOpening(toggledItem));
                    this.toggleStates = {...this.toggleStates, [previouslyOpenedIndex]: ToggleState.CLOSE};
                    this.toggleStates = {...this.toggleStates, [index]: ToggleState.OPEN};
                }
            } else {
                this.scrollToItem(toggledItem, this.offsetFromVerticalAlignmentWhenOpening(toggledItem));
                this.toggleStates = {...this.toggleStates, [index]: ToggleState.OPEN};
            }
        }
    }

    private getPreviouslyOpenedItem(openingIndex: number): Nullable<number> {
        const previouslyOpenedIndex = Object.entries(this.toggleStates)
            .find(([index, toggleState]) => toggleState === ToggleState.OPEN && Number(index) !== openingIndex)?.[0];
        return previouslyOpenedIndex !== undefined ? Number(previouslyOpenedIndex) : null;
    }

    private calculateClosedMarginBetween(openingItem: HTMLElement, closingItem: HTMLElement): number {
        if (this.openingItemBeforeClosingItem(openingItem, closingItem)) {
            return closingItem.style.getPropertyValue("--collapsible-height").toInt();
        }
        return 0;
    }

    private openingItemBeforeClosingItem(openingItem: HTMLElement, closingItem: HTMLElement): boolean {
        return closingItem.compareDocumentPosition(openingItem) === this.DOCUMENT_POSITION_FOLLOWING;
    }

    private offsetFromVerticalAlignmentWhenOpening(toggledItem: HTMLElement): number {
        return this.currentlyVerticalAligned() ? 0.5 * this.getHeight(toggledItem) : 0;
    }

    private offsetFromVerticalAlignmentWhenClosing(toggledItem: HTMLElement): number {
        return this.currentlyVerticalAligned() ? -0.5 * this.getHeight(toggledItem) : 0;
    }

    private currentlyVerticalAligned(): boolean {
        if (!this.verticalAligned) {
            return false;
        }
        return this.computedStyle().getPropertyValue("--grid-columns").toInt() !== 12;
    }

    private getHeight(item: HTMLElement): number {
        return item.style.getPropertyValue("--collapsible-height").toInt();
    }

    private scrollToItem(item: HTMLElement, closedMargin: number): void {
        void this.scrollService.scrollToElement(item, closedMargin + NEGATIVE_MARGIN, EXPANSION_DURATION, EXPANSION_DURATION)
            .then(this.compensateLayoutBodyHeightChange());
    }

    private compensateLayoutBodyHeightChange() {
        if (this.currentlyVerticalAligned()) {
            return () => this.scrollService.scrollRelative(0.5 * (this.layoutElement.clientHeight - this.previousLayoutElementHeight), EXPANSION_DURATION);
        } else {
            return () => noop();
        }
    }
}